import { useState } from 'react';

export default function LeadCaptureForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    incidentType: '',
    description: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('incidentType', formData.incidentType);
      formBody.append('description', formData.description);

      const response = await fetch('https://readdy.ai/api/form/d5qmlrahs78ip5370a20', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          incidentType: '',
          description: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-16 md:py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-[#D2691E]/10 px-4 py-2 rounded-full mb-4">
            <i className="ri-translate-2 w-5 h-5 flex items-center justify-center text-[#D2691E]"></i>
            <span className="text-[#D2691E] text-sm font-medium">Se Habla Español</span>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Get Your Free Consultation
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-600 mb-2">
            No fees unless we win your case
          </p>
          <p className="text-sm sm:text-base text-gray-500 italic">
            Consulta gratis • Sin cargos a menos que ganemos su caso
          </p>
        </div>

        <form 
          id="las-cruces-injury-claim"
          data-readdy-form
          onSubmit={handleSubmit}
          className="bg-white p-6 sm:p-8 md:p-12 rounded-2xl shadow-2xl"
        >
          <div className="mb-5">
            <label htmlFor="name" className="block text-base md:text-lg font-bold text-gray-900 mb-2">
              Full Name *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              required
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="w-full px-4 py-3 text-base border-2 border-gray-300 rounded-lg focus:border-[#D2691E] focus:outline-none transition-colors"
              placeholder="John Doe"
            />
          </div>

          <div className="mb-5">
            <label htmlFor="email" className="block text-base md:text-lg font-bold text-gray-900 mb-2">
              Email Address *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              className="w-full px-4 py-3 text-base border-2 border-gray-300 rounded-lg focus:border-[#D2691E] focus:outline-none transition-colors"
              placeholder="john@example.com"
            />
          </div>

          <div className="mb-5">
            <label htmlFor="phone" className="block text-base md:text-lg font-bold text-gray-900 mb-2">
              Phone Number *
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              required
              value={formData.phone}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
              className="w-full px-4 py-3 text-base border-2 border-gray-300 rounded-lg focus:border-[#D2691E] focus:outline-none transition-colors"
              placeholder="(575) 555-0123"
            />
          </div>

          <div>
            <label htmlFor="case-type" className="block text-sm font-medium text-gray-700 mb-1">
              Type of Case *
            </label>
            <select
              id="case-type"
              name="case-type"
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select case type</option>
              <option value="car-accident">Car Accident</option>
              <option value="workplace-injury">Workplace Injury</option>
              <option value="personal-injury">Personal Injury</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div className="mb-6">
            <label htmlFor="description" className="block text-base md:text-lg font-bold text-gray-900 mb-2">
              Brief Description of Your Case *
            </label>
            <textarea
              id="description"
              name="description"
              required
              rows={4}
              maxLength={500}
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="w-full px-4 py-3 text-base border-2 border-gray-300 rounded-lg focus:border-[#D2691E] focus:outline-none transition-colors resize-none"
              placeholder="Please describe what happened..."
            ></textarea>
            <p className="text-xs md:text-sm text-gray-500 mt-2">Maximum 500 characters</p>
          </div>

          {submitStatus === 'success' && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm md:text-base text-green-800 font-semibold">
                Thank you! Your case has been submitted successfully. We'll contact you within 24 hours.
              </p>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm md:text-base text-red-800 font-semibold">
                Something went wrong. Please try again or call us directly.
              </p>
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-[#2D5016] text-white px-8 py-4 md:px-12 md:py-5 rounded-full text-base sm:text-lg md:text-xl font-extrabold hover:bg-[#3D6B1F] transition-all duration-300 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
          >
            {isSubmitting ? 'Submitting...' : 'Value Your Claim'}
          </button>
        </form>
      </div>
    </section>
  );
}
